import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { routingAPI, driversAPI, trucksAPI } from '../../services/api';
import Modal from '../Common/Modal';

const RoutePlannerList = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [depots, setDepots] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    plan_date: new Date().toISOString().slice(0, 10),
    depot_id: '',
    driver_id: '',
    truck_id: '',
    notes: '',
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterDate ? { plan_date: filterDate } : {};
      const [p, d, dr, tr] = await Promise.all([
        routingAPI.listPlans(params),
        routingAPI.listDepots(),
        driversAPI.getAll(),
        trucksAPI.getAll(),
      ]);
      setPlans(p);
      setDepots(d);
      setDrivers(dr);
      setTrucks(tr);
      const defaultDepot = d.find((x) => x.is_default) || d[0];
      if (defaultDepot) {
        setForm((f) =>
          f.depot_id ? f : { ...f, depot_id: String(defaultDepot.id) }
        );
      }
    } catch (e) {
      alert('Error loading route plans: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [filterDate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const created = await routingAPI.createPlan({
        plan_date: form.plan_date,
        depot_id: parseInt(form.depot_id, 10),
        driver_id: form.driver_id ? parseInt(form.driver_id, 10) : null,
        truck_id: form.truck_id ? parseInt(form.truck_id, 10) : null,
        notes: form.notes || null,
      });
      setShowModal(false);
      navigate(`/route-planner/${created.id}`);
    } catch (e) {
      alert('Error creating plan: ' + e.message);
    }
  };

  if (loading && !plans.length) return <div className="loading">Loading route plans...</div>;

  return (
    <div className="management-container">
      <div className="page-header">
        <h1>Route Planner</h1>
        <button type="button" className="btn-primary" onClick={() => setShowModal(true)}>
          New route plan
        </button>
      </div>

      <div className="filter-bar" style={{ marginBottom: '1rem' }}>
        <label>
          Filter by date{' '}
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
          />
        </label>
        <button type="button" className="btn-secondary" onClick={() => setFilterDate('')}>
          Clear
        </button>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Depot</th>
            <th>Stops</th>
            <th>Status</th>
            <th>Distance</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {plans.length === 0 ? (
            <tr>
              <td colSpan={6}>No route plans yet.</td>
            </tr>
          ) : (
            plans.map((p) => (
              <tr key={p.id}>
                <td>{String(p.plan_date).slice(0, 10)}</td>
                <td>{p.depot_name}</td>
                <td>{p.stop_count}</td>
                <td>{p.status}</td>
                <td>
                  {p.total_distance_m != null
                    ? `${(p.total_distance_m / 1609.34).toFixed(1)} mi`
                    : '—'}
                </td>
                <td>
                  <Link to={`/route-planner/${p.id}`}>Open</Link>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <Modal
        show={showModal}
        title="New route plan"
        onClose={() => setShowModal(false)}
      >
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label>Plan date</label>
              <input
                type="date"
                required
                value={form.plan_date}
                onChange={(e) => setForm({ ...form, plan_date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Depot</label>
              <select
                required
                value={form.depot_id}
                onChange={(e) => setForm({ ...form, depot_id: e.target.value })}
              >
                <option value="">Select depot</option>
                {depots.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Driver</label>
              <select
                value={form.driver_id}
                onChange={(e) => setForm({ ...form, driver_id: e.target.value })}
              >
                <option value="">—</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.first_name} {d.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Truck</label>
              <select
                value={form.truck_id}
                onChange={(e) => setForm({ ...form, truck_id: e.target.value })}
              >
                <option value="">—</option>
                {trucks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.truck_number}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <button type="submit" className="btn-primary">
              Create & open
            </button>
          </form>
      </Modal>
    </div>
  );
};

export default RoutePlannerList;
